import { clamp } from 'lodash';

import type { ActuateGripperStepDatabase } from '@sb/integrations/implementations/gripper-general/step-wizard/ActuateGripperStepDatabase';

import type { SchunkEGxConfiguration } from '../types/SchunkEGxConfiguration';
import { schunkEGxVariantInfo } from '../utils/schunkEGxVariantInfo';

export function generateCommandInfo(
  gripperConfiguration: SchunkEGxConfiguration,
  stepArgs: ActuateGripperStepDatabase.Arguments,
) {
  const {
    isSoftGripSupported,
    isStrongGripSupported,
    isGPESupported,
    maxStrongGripForce,
    minSpeed,
    maxSpeed,
    maxGripSpeed,
    minPosition,
    maxPosition,
  } = schunkEGxVariantInfo(
    gripperConfiguration.variant,
    gripperConfiguration.zeroPositionOffset,
  );

  const isGrip = stepArgs.waitForGripToContinue || stepArgs.isFlexGrip;
  const isInwardGrip = stepArgs.gripKind === 'inward';

  const isSoftGrip = (isSoftGripSupported && stepArgs.isSoftGrip) ?? false;

  const isStrongGrip =
    (isStrongGripSupported && stepArgs.isStrongGrip) ?? false;

  const minPositionMM = Math.ceil(minPosition * 1000);
  const maxPositionMM = Math.floor(maxPosition * 1000);

  const bPosition = isInwardGrip ? minPositionMM : maxPositionMM;

  const diameterMM = clamp(
    stepArgs.isFlexGrip ? bPosition : stepArgs.diameterMM,
    minPositionMM,
    maxPositionMM,
  );

  // BasicGrip (inward) and SoftGrip use 50-100% force
  // StrongGrip uses 101-200% (maxStrongGripForce)
  const minForcePercent = isStrongGrip ? 1.01 : 0.5;
  const maxForcePercent = isStrongGrip ? maxStrongGripForce : 1;

  const forcePercent =
    isGrip || isInwardGrip
      ? clamp(stepArgs.forcePercent ?? 0.5, minForcePercent, maxForcePercent)
      : 0;

  // Speed is only used for SoftGrip and move-to-position
  // BasicGrip (inward) requires velocity = 0 (handled in command generation)
  const effectiveMaxSpeed = isSoftGrip ? maxGripSpeed : maxSpeed;
  const speed = clamp(stepArgs.speed ?? maxSpeed, minSpeed, effectiveMaxSpeed);

  return {
    isSoftGripSupported,
    isStrongGripSupported,
    isGPESupported,
    isGrip,
    isSoftGrip,
    isStrongGrip,
    minPositionMM,
    maxPositionMM,
    diameterMM,
    minForcePercent,
    maxForcePercent,
    forcePercent,
    minSpeed,
    maxSpeed: effectiveMaxSpeed,
    speed,
  };
}
